// @flow
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { filter, isEmpty } from 'lodash';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import MusicFoundationBanner from 'components/organisms/musicFoundationBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import PastEventsSection from 'components/templates/pastEvents';
import { EventSelector } from 'modules/event/selector';
import { EventActions } from 'modules/event/actions';
import { pastEventsIntro } from 'Mock/pastEvents';
import RenderSEO from 'components/atoms/renderSEO';
import { FooterSelector } from 'modules/footerLink/selector';
import { FooterActions } from 'modules/footerLink/actions';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const PastEventsPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    footerLink,
    getFooterLinkData,
    siteSettings,
    contact,
    getContactDetails,
    getBannerData,
    musicBanner,
    getPastEvents,
    getPastEventFilters,
    pastEvents,
    isFiltered,
    loadingMore,
  } = props;

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');

  useEffect(() => {
    getBannerData();
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getPastEvents(1, 25);
    getPastEventFilters();
    getFooterLinkData();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !musicBanner ||
    !pastEvents ||
    !footerLink
  ) {
    return null;
  }

  const selectedVertical = filter(verticals, ({ id }) => id === verticalId);
  const metaTagSEO =
    selectedVertical.length > 0 ? selectedVertical[0].metaTagSEO : '';

  const componentList = [
    ...(!isEmpty(musicBanner)
      ? [
          {
            component: MusicFoundationBanner,
            data: musicBanner,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: RouteNames.musicEvents,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(pastEvents) || isFiltered || loadingMore
      ? [
          {
            component: PastEventsSection,
            data: { pastEventsIntro, pastEvents, pastEventSection: true },
            theme: 'light-theme',
            sectionId: 'past-events',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Music & Events',
                breadcrumbLink: '#',
              },
              {
                breadcrumbName: 'Past Events',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },  
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Music & Events',
          breadcrumbLink: RouteNames.musicEvents,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} currentRouteName={'music-and-events'} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      FooterSelector.isLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    musicBanner: HomeSelector.getBanners(state, true, 'music', ''),
    pastEvents: EventSelector.getPastEvents(state),
    isFiltered: EventSelector.isEventsFiltered(state),
    loadingMore: EventSelector.getPastEventsLoadingMore(state),
  };
};
const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const {
    getPastEvents,
    getPastEventFilters,
  } = EventActions;
  const { getFooterLinkData } = FooterActions;
  return bindActionCreators(
    {
      getBannerData,
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getPastEvents,
      getPastEventFilters,
      getFooterLinkData,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(PastEventsPage);